import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useI18n } from '../../../../contexts/I18nContext';
import axios from '../../../../services/api';
import './TagSelector.scss';

const TagSelector = ({
  id,
  title,
  description,
  required = false,
  value,
  onChange,
  onValidationChange,
  disabled = false,
  className = '',
  props = {}
}) => {
  const { t, language } = useI18n();
  const [options, setOptions] = useState([]);
  const [isValid, setIsValid] = useState(true);
  const [validationMessage, setValidationMessage] = useState('');
  const [headerTitle, setHeaderTitle] = useState(title);
  const [headerDescription, setHeaderDescription] = useState(description);
  const validationCallbackRef = useRef(onValidationChange);

  const {
    dictName,
    options: propOptions,
    allowMultiple = false,
    defaultValue
  } = props;

  useEffect(() => {
    validationCallbackRef.current = onValidationChange;
  }, [onValidationChange]);

  useEffect(() => {
    let cancelled = false;

    const initOptions = async () => {
      if (propOptions && propOptions.length > 0) {
        setOptions(propOptions);
        return;
      }

      if (!dictName) {
        setOptions([]);
        return;
      }

      try {
        const apiLanguage = language === 'en' ? 'en' : 'tc';
        const response = await axios.get('/dictDetail', {
          params: {
            dictName,
            language: apiLanguage,
            page: 0,
            limit: 100
          }
        });
        const list = Array.isArray(response.data && response.data.data) ? response.data.data : [];
        if (cancelled) return;
        if (list.length > 0) {
          const dictMetaSource = list.find(item => item && item.dict && (item.dict.remark || item.dict.subtitle));
          if (dictMetaSource && dictMetaSource.dict) {
            const { remark, subtitle } = dictMetaSource.dict;
            if (remark) {
              setHeaderTitle(remark);
            }
            if (subtitle) {
              setHeaderDescription(subtitle);
            }
          }
          const sortedList = [...list].sort((a, b) => {
            const sa = typeof a.sort === 'number' ? a.sort : 0;
            const sb = typeof b.sort === 'number' ? b.sort : 0;
            return sa - sb;
          });
          const remoteOptions = sortedList.map(item => ({
            value: item.value,
            label: item.label,
            description: item.description || ''
          }));
          setOptions(remoteOptions);
        } else {
          setOptions([]);
        }
      } catch (e) {
        if (!cancelled) {
          setOptions([]);
        }
      }
    };

    initOptions();

    return () => {
      cancelled = true;
    };
  }, [dictName, language, propOptions]);

  useEffect(() => {
    if (!value && options.length > 0) {
      const initialValue = defaultValue || (allowMultiple ? [options[0].value] : options[0].value);
      if (initialValue && onChange) {
        onChange(initialValue);
      }
    }
  }, [options, value, defaultValue, allowMultiple, onChange]);

  const validateValue = useCallback((nextValue) => {
    let valid = true;
    let message = '';

    if (required) {
      if (!nextValue || (Array.isArray(nextValue) && nextValue.length === 0)) {
        valid = false;
        message = t('aiToolTemplate.parameterSelector.validation.required') || '';
      }
    }

    setIsValid(valid);
    setValidationMessage(message);

    const callback = validationCallbackRef.current;
    if (callback) {
      callback(valid, message);
    }
  }, [required, t]);

  useEffect(() => {
    validateValue(value);
  }, [value, validateValue]);

  const handleOptionClick = optionValue => {
    if (disabled) return;

    let nextValue;
    if (allowMultiple) {
      const currentValues = Array.isArray(value) ? value : [];
      if (currentValues.includes(optionValue)) {
        nextValue = currentValues.filter(v => v !== optionValue);
      } else {
        nextValue = [...currentValues, optionValue];
      }
    } else {
      nextValue = optionValue;
    }

    validateValue(nextValue);

    if (onChange) {
      onChange(nextValue);
    }
  };

  if (!options || options.length === 0) {
    return null;
  }

  const isSelected = optionValue => {
    if (allowMultiple) {
      return Array.isArray(value) && value.includes(optionValue);
    }
    return value === optionValue;
  };

  return (
    <div className={`tag-selector-container ${className} ${!isValid ? 'invalid' : ''}`}>
      <div className="tag-header">
        {headerTitle && (
          <h3 className="tag-title">
            {headerTitle}
            {required && <span className="required-indicator">*</span>}
          </h3>
        )}
        {headerDescription && <p className="tag-description">{headerDescription}</p>}
      </div>
      <div className="tag-selector-content">
        <div className="tag-options">
          {options.map(option => (
            <button
              key={option.value}
              type="button"
              className={`tag-option ${isSelected(option.value) ? 'active' : ''}`}
              onClick={() => handleOptionClick(option.value)}
              disabled={disabled}
              title={option.description}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      {!isValid && validationMessage && (
        <div className="tag-validation-message">
          {validationMessage}
        </div>
      )}
    </div>
  );
};

export default TagSelector;
